import { METER_KIND_LABELS, METER_UNITS } from '@acasa/shared'
import { newId, removeItem, type SectionProps, updateItem } from './body-utils'
import { AddButton, Counter, EntryCard, Field, TextArea, TextInput } from './fields'
import { PhotoStrip } from './photo-strip'
import { PreviousKeys, PreviousMeters } from './previous-values'

export function MetersSection({ protocol, body, patch, readOnly }: SectionProps) {
  return (
    <div className="space-y-3">
      <p className="text-muted text-xs">
        Zählernummer und Stand ablesen. Ein Foto des Zählers ist der beste Beleg, falls der Stand
        später bestritten wird.
      </p>

      <PreviousMeters protocol={protocol} body={body} patch={patch} readOnly={readOnly} />

      {body.meters.map((meter) => (
        <EntryCard
          key={meter.id}
          title={
            <input
              value={meter.label}
              disabled={readOnly}
              placeholder={METER_KIND_LABELS[meter.kind]}
              onChange={(event) =>
                patch((current) => ({
                  ...current,
                  meters: updateItem(current.meters, meter.id, { label: event.target.value }),
                }))
              }
              className="text-ink w-full bg-transparent text-sm font-medium outline-none"
            />
          }
          onRemove={
            readOnly
              ? undefined
              : () =>
                  patch((current) => ({ ...current, meters: removeItem(current.meters, meter.id) }))
          }
        >
          <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
            <Field label="Zählernummer">
              <TextInput
                disabled={readOnly}
                value={meter.serial}
                placeholder="z. B. 1ESY1161234567"
                onChange={(event) =>
                  patch((current) => ({
                    ...current,
                    meters: updateItem(current.meters, meter.id, { serial: event.target.value }),
                  }))
                }
              />
            </Field>

            <Field label={`Stand${meter.unit ? ` in ${meter.unit}` : ''}`}>
              <TextInput
                disabled={readOnly}
                // Not type=number: German readings carry a decimal comma and
                // leading zeros, both of which a number input mangles.
                inputMode="decimal"
                value={meter.value}
                placeholder={METER_UNITS[meter.kind] ? '0,0' : ''}
                onChange={(event) =>
                  patch((current) => ({
                    ...current,
                    meters: updateItem(current.meters, meter.id, { value: event.target.value }),
                  }))
                }
                className="tnum font-mono sm:w-40"
              />
            </Field>
          </div>

          <Field label="Anmerkung">
            <TextInput
              disabled={readOnly}
              value={meter.note}
              placeholder="z. B. Zähler schwer zugänglich"
              onChange={(event) =>
                patch((current) => ({
                  ...current,
                  meters: updateItem(current.meters, meter.id, { note: event.target.value }),
                }))
              }
            />
          </Field>

          <PhotoStrip
            protocol={protocol}
            section="meter"
            refKey={meter.id}
            readOnly={readOnly}
            label="Foto vom Zähler"
          />
        </EntryCard>
      ))}

      {!readOnly && (
        <AddButton
          onClick={() =>
            patch((current) => ({
              ...current,
              meters: [
                ...current.meters,
                {
                  id: newId(),
                  kind: 'other',
                  label: '',
                  serial: '',
                  value: '',
                  unit: '',
                  source: 'manual',
                  note: '',
                  photoIds: [],
                },
              ],
            }))
          }
        >
          Zähler hinzufügen
        </AddButton>
      )}
    </div>
  )
}

export function KeysSection({ protocol, body, patch, readOnly }: SectionProps) {
  const total = body.keys.reduce((sum, key) => sum + key.count, 0)

  return (
    <div className="space-y-3">
      <p className="text-muted text-xs">
        Anzahl der tatsächlich übergebenen Schlüssel je Art. Insgesamt{' '}
        <span className="text-ink tnum font-mono font-medium">{total}</span> Stück.
      </p>

      <PreviousKeys protocol={protocol} body={body} patch={patch} readOnly={readOnly} />

      {body.keys.map((key) => (
        <EntryCard
          key={key.id}
          title={
            <input
              value={key.label}
              disabled={readOnly}
              placeholder="Schlüsselart"
              onChange={(event) =>
                patch((current) => ({
                  ...current,
                  keys: updateItem(current.keys, key.id, { label: event.target.value }),
                }))
              }
              className="text-ink w-full bg-transparent text-sm font-medium outline-none"
            />
          }
          onRemove={
            readOnly
              ? undefined
              : () => patch((current) => ({ ...current, keys: removeItem(current.keys, key.id) }))
          }
        >
          <div className="flex flex-wrap items-end gap-3">
            <Field label="Anzahl">
              <Counter
                value={key.count}
                disabled={readOnly}
                onChange={(count) =>
                  patch((current) => ({
                    ...current,
                    keys: updateItem(current.keys, key.id, { count }),
                  }))
                }
              />
            </Field>

            <div className="min-w-[12rem] flex-1">
              <Field label="Bemerkung">
                <TextArea
                  rows={1}
                  disabled={readOnly}
                  value={key.note}
                  onChange={(event) =>
                    patch((current) => ({
                      ...current,
                      keys: updateItem(current.keys, key.id, { note: event.target.value }),
                    }))
                  }
                />
              </Field>
            </div>
          </div>
        </EntryCard>
      ))}

      {!readOnly && (
        <AddButton
          onClick={() =>
            patch((current) => ({
              ...current,
              keys: [...current.keys, { id: newId(), label: '', count: 1, note: '' }],
            }))
          }
        >
          Schlüsselart hinzufügen
        </AddButton>
      )}
    </div>
  )
}
